import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../utils/api';
import { debugLog } from '../../utils/config';

interface SystemHealthProps {
  onChainStatus: 'healthy' | 'warning' | 'error';
  offChainStatus: 'healthy' | 'warning' | 'error';
  lastUpdate: Date;
  mevProtectionStatus?: {
    status: 'active' | 'inactive' | 'error';
    threatsBlocked24h: number;
  };
  webhookStatus?: {
    lastReceived: number | null;
    totalEvents: number;
  };
}

interface ServiceStatus {
  name: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  icon: string;
  lastChecked: Date;
  details?: string;
}

export const SystemHealth: React.FC<SystemHealthProps> = ({
  onChainStatus,
  offChainStatus,
  lastUpdate,
  mevProtectionStatus,
  webhookStatus
}) => {
  const [lambdaCount, setLambdaCount] = useState<number>(0);
  const [lambdaStatus, setLambdaStatus] = useState<'healthy' | 'warning' | 'error'>('healthy');

  useEffect(() => {
    const fetchLambdaStatus = async () => {
      try {
        const functions = await api.lambda.getFunctionsStatus();
        const healthyFunctions = functions.filter(f => f.status === 'healthy');
        setLambdaCount(functions.length);

        // Determine overall Lambda health
        const healthPercentage = (healthyFunctions.length / functions.length) * 100;
        if (healthPercentage === 100) {
          setLambdaStatus('healthy');
        } else if (healthPercentage >= 80) {
          setLambdaStatus('warning');
        } else {
          setLambdaStatus('error');
        }
      } catch (err) {
        debugLog('Lambda status fetch error:', err);
        setLambdaCount(0);
        setLambdaStatus('error');
      }
    };

    fetchLambdaStatus();
    const interval = setInterval(fetchLambdaStatus, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const services: ServiceStatus[] = [
    {
      name: 'Treasury Contract',
      status: onChainStatus,
      icon: 'bi-shield-check',
      lastChecked: lastUpdate,
      details: onChainStatus === 'healthy' ? 'All functions operational' : 'Minor delays detected'
    },
    {
      name: 'Market Making Vault',
      status: onChainStatus,
      icon: 'bi-database',
      lastChecked: lastUpdate,
      details: 'Active trading enabled'
    },
    {
      name: 'Trading Engine',
      status: offChainStatus,
      icon: 'bi-activity',
      lastChecked: lastUpdate,
      details: 'Grid strategy running'
    },
    {
      name: 'Lambda Functions',
      status: lambdaStatus,
      icon: 'bi-cloud',
      lastChecked: lastUpdate,
      details: lambdaCount > 0 ? `${lambdaCount} functions online` : 'Loading...'
    },
    {
      name: 'MEV Protection',
      status: mevProtectionStatus?.status === 'active' ? 'healthy' :
              mevProtectionStatus?.status === 'error' ? 'error' : 'warning',
      icon: 'bi-shield-fill-check',
      lastChecked: lastUpdate,
      details: mevProtectionStatus
        ? `${mevProtectionStatus.threatsBlocked24h} threats blocked (24h)`
        : 'Loading...'
    },
    {
      name: 'Revenue Webhook',
      status: webhookStatus?.lastReceived && (Date.now() - webhookStatus.lastReceived < 86400000)
        ? 'healthy' :
        webhookStatus?.lastReceived ? 'warning' : 'offline',
      icon: 'bi-database-check',
      lastChecked: lastUpdate,
      details: webhookStatus?.lastReceived
        ? `Last event ${formatDistanceToNow(new Date(webhookStatus.lastReceived), { addSuffix: true })}`
        : webhookStatus ? 'No events yet' : 'Loading...'
    }
  ];

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bi-check-circle-fill';
      case 'warning':
        return 'bi-exclamation-triangle-fill';
      case 'error':
        return 'bi-x-circle-fill';
      case 'offline':
        return 'bi-clock-fill';
      default:
        return 'bi-check-circle-fill';
    }
  };

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-danger';
      case 'offline':
        return 'text-secondary';
      default:
        return 'text-secondary';
    }
  };

  const getStatusBgColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-success-subtle';
      case 'warning':
        return 'bg-warning-subtle';
      case 'error':
        return 'bg-danger-subtle';
      case 'offline':
        return 'bg-secondary-subtle';
      default:
        return 'bg-secondary-subtle';
    }
  };

  const healthyCount = services.filter(s => s.status === 'healthy').length;
  const totalCount = services.length;
  const healthPercentage = Math.round((healthyCount / totalCount) * 100);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-transparent">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h5 className="card-title mb-1">System Health</h5>
            <p className="text-muted small mb-0">
              Last updated {lastUpdate && !isNaN(lastUpdate.getTime())
                ? formatDistanceToNow(lastUpdate, { addSuffix: true })
                : 'just now'}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className={`rounded-circle ${
              healthPercentage === 100 ? 'bg-success' :
              healthPercentage >= 80 ? 'bg-warning' : 'bg-danger'
            }`} style={{ width: '12px', height: '12px' }}></div>
            <span className="fw-semibold">{healthPercentage}%</span>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="d-flex flex-column gap-3">
          {services.map((service, index) => {
            const statusIcon = getStatusIcon(service.status);

            return (
              <div
                key={index}
                className="d-flex align-items-center justify-content-between p-3 rounded border border-light"
              >
                <div className="d-flex align-items-center gap-3">
                  <div className={`p-2 rounded ${getStatusBgColor(service.status)}`}>
                    <i className={`bi ${service.icon} text-muted`}></i>
                  </div>

                  <div>
                    <p className="mb-0 fw-medium">
                      {service.name}
                    </p>
                    {service.details && (
                      <p className="text-muted small mb-0">
                        {service.details}
                      </p>
                    )}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <i className={`bi ${statusIcon} ${getStatusColor(service.status)}`}></i>
                  <span className={`small fw-medium ${getStatusColor(service.status)}`}>
                    {service.status ? service.status.charAt(0).toUpperCase() + service.status.slice(1) : 'Unknown'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
